import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
// import curve_line from '../../assets/images/curve_line.png';

const MODELS = [
  "GPT-5.5",
  "GPT-5.2",
  "Claude", 
  "DeepSeek",
  "Grok",
  "Perplexity",
  "Gemini",
  "Kimi",
  "Flux",
  "Kling",
  "Veo",
  "Seedance",
  "Sora",
  "Lyria",
  "Wan"
];

const MODEL_TEXT_CLASS =
  "bg-gradient-to-r from-[#2874FA] to-[#22A4FF] bg-clip-text text-transparent lg:text-[60px] text-[28px] relative z-10 inter-font whitespace-nowrap block leading-[1.1] font-bold";
const ROW_CLASS = "flex items-center py-2 leading-none font-bold";

export default function VerticalCarousel({ interval = 2000 }) {
  const [index, setIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [carouselWidth, setCarouselWidth] = useState(0);
  const [rowHeightPx, setRowHeightPx] = useState(0);
  const [windowWidth, setWindowWidth] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const modelRef = useRef(null);
  const measureRowRef = useRef(null);
  const timeoutRef = useRef(null);

  const extendedModels = useMemo(() => [...MODELS, MODELS[0]], []);
  const currentModel = extendedModels[index];

  // Add window resize handling and initial window size detection
  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        setWindowWidth(window.innerWidth);
      }
    };

    // Set initial window width
    if (typeof window !== "undefined") {
      setWindowWidth(window.innerWidth);
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);
      
      const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
      mediaQuery.addEventListener('change', handleChange);
      
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  const getRowHeightRem = useCallback(() => {
    if (rowHeightPx > 0) {
      const rootFontSize =
        parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      return rowHeightPx / rootFontSize;
    }

    if (windowWidth > 0) {
      if (windowWidth < 500) return 2.75;
      if (windowWidth < 640) return 2.75;
      if (windowWidth < 768) return 3.5;
    }
    return 5;
  }, [rowHeightPx, windowWidth]);

  useLayoutEffect(() => {
    const measure = () => {
      const measureEl = measureRowRef.current;
      const activeEl = modelRef.current;
      const height = Math.ceil(
        measureEl?.getBoundingClientRect().height ||
          activeEl?.closest("[data-carousel-row]")?.getBoundingClientRect().height ||
          activeEl?.getBoundingClientRect().height ||
          0
      );

      if (height > 0) {
        setRowHeightPx(height);
      }

      if (activeEl) {
        setCarouselWidth(Math.ceil(activeEl.getBoundingClientRect().width));
      }
    };

    measure();
    const rafId = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", measure);
    };
  }, [index, windowWidth, currentModel]);

  const rowHeight = getRowHeightRem();
  const viewportHeight = rowHeightPx || undefined;

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setIndex((prev) => {
        const nextIndex = prev + 1;
        if (nextIndex === MODELS.length) {
          setIsTransitioning(true);
          return nextIndex;
        } else if (nextIndex > MODELS.length) {
          setIsTransitioning(false);
          return 0; 
        } else {
          setIsTransitioning(true);
          return nextIndex;
        }
      });
    }, interval);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [index, interval]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (index === 0 && !isTransitioning) {
      const resetTimeout = setTimeout(() => {
        setIsTransitioning(true); 
      }, 50);
      return () => clearTimeout(resetTimeout);
    }
  }, [index, isTransitioning]);

  const handleTransitionEnd = (e) => {
    try {
      if (e?.propertyName !== "transform") return;
      if (index === MODELS.length) {
        setIsTransitioning(false);
        setIndex(0);
      }
    } catch (error) {
      console.warn('Carousel transition error:', error);
      // Fallback: reset to first item
      setIsTransitioning(false);
      setIndex(0);
    }
  };

  return (
    <div className="relative w-full mt-2 sm:mt-0 md:mt-2 inter-font overflow-visible">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute opacity-0 -z-10"
      >
        <div ref={measureRowRef} className={ROW_CLASS}>
          <span className={MODEL_TEXT_CLASS}>Lyria</span>
        </div>
      </div>
      <div
        className="flex mt-[-15px] items-center justify-center gap-1 md:gap-2 overflow-visible leading-none font-bold"
        style={{ minHeight: viewportHeight ? `${viewportHeight}px` : undefined }}
      >
        <span
          className={`text-black lg:text-[60px] text-[28px] inter-font shrink-0 leading-[1.1] font-bold ${
            !prefersReducedMotion ? 'transition-transform duration-300 ease-in-out' : ''
          }`}
          style={{
            display: "inline-block",
            whiteSpace: "nowrap",
          }}
        >
          Developed on
        </span>
        <div
          className="overflow-hidden shrink-0 transition-[width] duration-300 ease-in-out"
          style={{ width: carouselWidth ? `${carouselWidth}px` : 'max-content' }}
        >
          <div
            className="overflow-hidden"
            style={{ height: viewportHeight ? `${viewportHeight}px` : undefined }}
          >
            <div
              className={`flex flex-col items-start inter-font ${
                isTransitioning && !prefersReducedMotion
                  ? "transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
                  : ""
              }`}
              style={{
                transform: rowHeightPx
                  ? `translateY(-${index * rowHeightPx}px)`
                  : `translateY(-${index * rowHeight}rem)`,
              }}
              onTransitionEnd={handleTransitionEnd}
            >
              {extendedModels.map((model, i) => (
                <div
                  key={i}
                  data-carousel-row
                  className={ROW_CLASS}
                  style={{ height: viewportHeight ? `${viewportHeight}px` : undefined }}
                >
                  <span
                    ref={i === index ? modelRef : null}
                    className={MODEL_TEXT_CLASS}
                  >
                    {model}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}